import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary = hi-vis amber brand
        default:
          "bg-primary text-primary-foreground font-semibold shadow-[0_1px_0_hsl(var(--foreground)/0.15)_inset,0_2px_8px_hsl(var(--primary)/0.25)] hover:bg-primary/90 hover:shadow-[0_1px_0_hsl(var(--foreground)/0.2)_inset,0_4px_14px_hsl(var(--primary)/0.4)] active:translate-y-px",
        destructive:
          "bg-danger text-danger-foreground font-semibold hover:bg-danger/90 shadow-[0_2px_8px_hsl(var(--danger)/0.25)]",
        outline:
          "border border-border bg-transparent text-foreground hover:border-primary/60 hover:text-primary hover:bg-primary/5",
        secondary:
          "bg-secondary text-secondary-foreground border border-border hover:bg-secondary/70",
        ghost:
          "text-foreground/80 hover:bg-muted hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero:
          "bg-primary text-primary-foreground font-semibold shadow-[0_4px_18px_hsl(var(--primary)/0.35)] hover:brightness-110 hover:shadow-[0_6px_24px_hsl(var(--primary)/0.5)] transition-all duration-300",
        heroOutline:
          "border border-foreground/20 text-foreground hover:border-primary/60 hover:text-primary font-semibold transition-all duration-300",
        success:
          "bg-success text-success-foreground hover:bg-success/90",
        warning:
          "bg-warning text-warning-foreground hover:bg-warning/90",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-6",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
